
import { Link, useLocation, useNavigate } from "react-router-dom";
import smarterBrighter from "@/assets/smarter-brighter.png";

interface FooterProps {
  logo: React.ReactNode;
  brandName: string;
  socialLinks: Array<{
    icon: React.ReactNode;
    href: string;
    label: string;
  }>;
  mainLinks: Array<{
    href: string;
    label: string;
  }>;
  legalLinks: Array<{
    href: string;
    label: string;
  }>;
  copyright: {
    text: string;
    license?: string;
  };
}
export function Footer({
  logo,
  brandName,
  socialLinks,
  mainLinks,
  legalLinks,
  copyright
}: FooterProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';

  const handleNavigation = (e: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
    e.preventDefault();
    
    if (!isHomePage) {
      // If not on home page, navigate to home first, then scroll to section
      navigate('/', { state: { scrollTo: sectionId } });
      return;
    }
    
    if (sectionId === 'top') {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      return;
    }
    
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleLearnNavigation = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    navigate('/learn');
  };

  return <footer className="bg-quickatoo-charcoal pb-6 pt-16 text-white lg:pb-8 lg:pt-20">
      <div className="px-4 lg:px-8">
        <div className="md:flex md:items-end md:justify-between">
          <a href="#" onClick={(e) => handleNavigation(e, 'top')} className="flex items-center gap-x-2" aria-label={brandName}>
            {logo}
          </a>
          <img src={smarterBrighter} alt="Smarter. Brighter." className="mt-8 h-7 w-auto md:mt-0 md:h-8" />
        </div>
        <div className="border-t border-white/15 mt-8 pt-6 md:pt-8 lg:grid lg:grid-cols-10">
          <nav className="lg:mt-0 lg:col-[4/11]">
            <ul className="list-none flex flex-wrap -my-1 -mx-2 lg:justify-end">
              <li className="my-1 mx-2 shrink-0">
                <a href="#" onClick={(e) => handleNavigation(e, 'top')} className="text-sm font-medium text-white/90 underline-offset-4 hover:text-white hover:underline">
                  Home
                </a>
              </li>
              <li className="my-1 mx-2 shrink-0">
                <a href="#why-quickatoo" onClick={(e) => handleNavigation(e, 'why-quickatoo')} className="text-sm font-medium text-white/90 underline-offset-4 hover:text-white hover:underline">
                  Why Quickatoo
                </a>
              </li>
              <li className="my-1 mx-2 shrink-0">
                <a href="#about" onClick={(e) => handleNavigation(e, 'about')} className="text-sm font-medium text-white/90 underline-offset-4 hover:text-white hover:underline">
                  About
                </a>
              </li>
              <li className="my-1 mx-2 shrink-0">
                <a href="#faq" onClick={(e) => handleNavigation(e, 'faq')} className="text-sm font-medium text-white/90 underline-offset-4 hover:text-white hover:underline">
                  FAQ
                </a>
              </li>
              <li className="my-1 mx-2 shrink-0">
                <a href="#contact" onClick={(e) => handleNavigation(e, 'contact')} className="text-sm font-medium text-white/90 underline-offset-4 hover:text-white hover:underline">
                  Contact
                </a>
              </li>
              <li className="my-1 mx-2 shrink-0">
                <a href="#learn" onClick={handleLearnNavigation} className="text-sm font-medium text-white/90 underline-offset-4 hover:text-white hover:underline">
                  Learn
                </a>
              </li>
            </ul>
          </nav>
          <div className="mt-6 lg:mt-0 lg:col-[4/11]">
            <ul className="list-none flex flex-wrap -my-1 -mx-3 lg:justify-end">
              {legalLinks.map((link, i) => (
                <li key={i} className="my-1 mx-3 shrink-0">
                  <Link to={link.href} className="text-sm text-white/60 underline-offset-4 hover:text-white hover:underline">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="mt-6 text-sm leading-6 text-white/60 whitespace-nowrap lg:mt-0 lg:row-[1/3] lg:col-[1/4]">
            <div>{copyright.text}</div>
            {copyright.license && <div>{copyright.license}</div>}
          </div>
        </div>
      </div>
    </footer>;
}
